// Globals
import { Suspense, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BaseSelectRef } from 'rc-select';

// Store
import { useAppState, useAppDispatch } from '@/store';

// Modules
import {
  getChannelsOptions,
  selectChannel,
  selectChannelType,
  useGetChannels,
  useGetDefaultChannel,
} from '@/modules/Channels';
import { getSettings, selectSettings, selectSettingsIsLoading } from '@/modules/Settings';
import { logOut } from '@/modules/Auth';
import { getMetatags, GetMetatagsDTO } from '@/modules/Metatags';
import { getRedirects, GetRedirectsDTO } from '@/modules/Redirects';
import { getRobotsTxt } from '@/modules/RobotsTxt';
import { getVariables, GetVariablesDTO } from '@/modules/Variables';
import { getCategoriesTree } from '@/modules/Categories';
import { getDeliverySettings } from '@/modules/DeliverySettings';
import { getLinkingTools } from '@/modules/LinkingTool';
import { getBlogPosts } from '@/modules/Blog/feature';
import { getPaymentMethods } from '@/modules/PaymentMethods';
import { getCoupons } from '@/modules/Coupons';

// Models
import { ChannelType } from '@/models/channel.model';

// Components
import { Image } from '@/components';
import { Button, Select, Spinner } from '@/ui';
import { Menu, Sidebar } from '../../components';
import MainLayoutContent from '../MainLayoutContent/MainLayoutContent';
import { StyledImageHolder, StyledLayout, StyledLogoutButton, StyledTopBlock } from './MainLayoutStyles';

// Helpers
import { getMenuConfig } from '../../helpers/menuConfig';

const MainLayout = () => {
  const selectRef = useRef<BaseSelectRef>(null);
  const { t } = useTranslation(['common']);
  const { activeChannels, isLoading } = useGetChannels();
  const selectedChannel = useAppState(selectChannel);
  const navigate = useNavigate();
  const channelType = useAppState(selectChannelType);
  const settings = useAppState(selectSettings);
  const isSettingsLoading = useAppState(selectSettingsIsLoading);
  const { onChangeChannel } = useGetDefaultChannel();

  const dispatch = useAppDispatch();
  const path = useLocation();

  const handleChange = async (value: number) => {
    const channel = activeChannels?.find(({ id }) => id === value);
    if (!channel) return;
    onChangeChannel(channel);
    const metaTagsDto = new GetMetatagsDTO({ page: '1', channelId: channel.id });
    const redirectsDto = new GetRedirectsDTO({ page: 1, channelId: channel.id });
    const dto = new GetVariablesDTO({ page: 1, channelId: channel.id });
    const promises: unknown[] = [
      dispatch(getSettings({ channel: channel.slug })),
      dispatch(getLinkingTools(redirectsDto)),
      dispatch(getMetatags(metaTagsDto)),
      dispatch(getRedirects(redirectsDto)),
      dispatch(getRobotsTxt({ channel: channel.slug })),
      dispatch(getVariables(dto)),
      dispatch(getCategoriesTree({ channelId: channel.id })),
      dispatch(getDeliverySettings({ channelId: channel.id })),
      dispatch(
        getBlogPosts({ page: 1, pageSize: 10, channelSlug: channel.slug, withDrafts: true, injectVariables: false }),
      ),
      dispatch(getPaymentMethods({ channelId: channel.id })),
      dispatch(getCoupons({ channelId: channel.id })),
    ];

    navigate('/');
    await Promise.all(promises);
  };

  const handleSelect = () => selectRef.current?.blur();

  const handleLogout = () => {
    dispatch(logOut());
  };

  const options = useMemo(() => getChannelsOptions(activeChannels), [activeChannels]);
  const logo = settings?.images?.find(({ metaType }) => metaType === 'headerLogo')?.file.url;

  return (
    <StyledLayout>
      <Sidebar>
        <StyledTopBlock $channelType={selectedChannel?.type}>
          <Spinner size="small" spinning={isSettingsLoading}>
            <StyledImageHolder>
              <Image src={logo ?? ''} />
            </StyledImageHolder>
          </Spinner>
          {activeChannels && (
            <Select
              ref={selectRef}
              showSearch
              loading={isLoading}
              options={options}
              value={selectedChannel?.id}
              onChange={handleChange}
              onSelect={handleSelect}
            />
          )}
        </StyledTopBlock>
        <Menu items={getMenuConfig(t, channelType === ChannelType.SHOWCASE)} selectedKey={path.pathname} />
        <StyledLogoutButton>
          <Button size="small" type="primary" block onClick={handleLogout}>
            {t('logout')}
          </Button>
        </StyledLogoutButton>
      </Sidebar>
      <MainLayoutContent>
        <Suspense fallback={<Spinner />}>
          <Outlet />
        </Suspense>
      </MainLayoutContent>
    </StyledLayout>
  );
};

export default MainLayout;
